import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { refreshInventory, getLowStockAlerts, LowStockItem } from '../../services/inventoryService';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './InventoryManager.css';
import type { User } from 'firebase/auth';

// TypeScript types
interface Product {
  id: string;
  sku?: string;
  title?: string;
  name?: string;
  brand?: string;
  category?: string;
  priceUSD?: number;
  price?: string;
  stock?: number;
  quantityInStock?: string;
  image?: string;
  popularityScore?: string;
  [key: string]: string | number | undefined;
}

interface InventoryManagerProps {
  user?: User | null;
  language?: string;
  onClose?: () => void;
}

const InventoryManager: React.FC<InventoryManagerProps> = ({ language = 'en', onClose }) => {
  const { t } = useTranslation('admin');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lowStockAlerts, setLowStockAlerts] = useState<LowStockItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadProducts();
    // Refresh every 10 seconds to show real-time updates
    const interval = setInterval(loadProducts, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async () => {
    try {
      // Refresh inventory cache first
      await refreshInventory();
      
      const response = await fetch('/catalogs/heseda-catalog.csv');
      const csvText = await response.text();
      
      // Parse CSV manually
      const lines = csvText.split('\n');
      const headers = lines[0].split(',');
      
      const parsedProducts: Product[] = lines.slice(1)
        .filter(line => line.trim())
        .map(line => {
          const values = line.split(',');
          const product: Record<string, string | number> = {};
          headers.forEach((header, index) => {
            product[header.trim()] = values[index]?.trim() || '';
          });
          return product as unknown as Product;
        });

      setProducts(parsedProducts);
      
      // Load low stock alerts
      const alerts = await getLowStockAlerts(10);
      setLowStockAlerts(alerts);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(t('inventoryMgr.loadFailed'));
      setLoading(false);
    }
  };

  const updateQuantity = (productId: string, newQuantity: string) => {
    setProducts(products.map(p => 
      p.id === productId 
        ? { 
            ...p, 
            quantityInStock: newQuantity,
            inStock: parseInt(newQuantity, 10) > 0 ? 'true' : 'false'
          }
        : p
    ));
    setHasChanges(true);
  };

  const updatePopularity = (productId: string, newScore: string) => {
    setProducts(products.map(p => 
      p.id === productId 
        ? { ...p, popularityScore: newScore }
        : p
    ));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/updateInventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          products: products.map(p => ({
            id: p.id,
            quantityInStock: p.quantityInStock,
            inStock: p.inStock,
            popularityScore: p.popularityScore
          })),
          userId: 'admin' // TODO: Get from auth context
        })
      });

      const result = await response.json();

      if (result.success) {
        setHasChanges(false);
        alert(t('inventoryMgr.saveSuccess'));
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert(t('inventoryMgr.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const categories = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="inventory-manager">
        <div className="inventory-header">
          <h2>📦 {t('inventoryMgr.title')}</h2>
          <button onClick={onClose} className="close-inventory">×</button>
        </div>
        <div className="loading">
          {t('inventoryMgr.loadingInventory')}
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-manager">
      <div className="inventory-header">
        <h2>📦 {t('inventoryMgr.title')}</h2>
        <div className="header-actions">
          <button onClick={loadProducts} className="refresh-button" title={t('inventoryMgr.refresh')}>
            🔄
          </button>
          <button onClick={onClose} className="close-inventory">×</button>
        </div>
      </div>

      {error && <div className="inventory-error">{error}</div>}
      
      {lowStockAlerts.length > 0 && (
        <div className="alerts-banner">
          <span className="alert-icon">⚠️</span>
          <span>
            {t('inventoryMgr.outOfStockCount', { count: lowStockAlerts.filter(a => a.status === 'out').length })}, {t('inventoryMgr.lowStockCount', { count: lowStockAlerts.filter(a => a.status === 'low').length })}
          </span>
        </div>
      )}

      <div className="inventory-controls">
        <input
          type="text"
          placeholder={t('inventoryMgr.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="category-filter"
        >
          <option value="all">
            {t('inventoryMgr.allCategories')}
          </option>
          {categories.filter(c => c !== 'all').map(category => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      <div className="inventory-stats">
        <div className="stat-card">
          <div className="stat-value">{products.length}</div>
          <div className="stat-label">{t('inventoryMgr.totalProducts')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {products.reduce((sum, p) => sum + (parseInt(p.quantityInStock || '0') || 0), 0)}
          </div>
          <div className="stat-label">{t('inventoryMgr.totalUnits')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {products.filter(p => (parseInt(p.quantityInStock || '0') || 0) < 10).length}
          </div>
          <div className="stat-label">{t('inventoryMgr.lowStock')}</div>
        </div>
      </div>

      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>{t('inventoryMgr.headerProduct')}</th>
              <th>{t('inventoryMgr.headerCategory')}</th>
              <th>{t('inventoryMgr.headerPrice')}</th>
              <th>{t('inventoryMgr.headerQuantity')}</th>
              <th>{t('inventoryMgr.headerPopularity')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => {
              const quantity = parseInt(product.quantityInStock || '0') || 0;
              return (
                <tr key={product.id} className={quantity === 0 ? 'out-of-stock-row' : quantity < 10 ? 'low-stock-row' : ''}>
                  <td>
                    <div className="product-cell">
                      {product.image && (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="product-thumb"
                        />
                      )}
                      <div>
                        <div className="product-name">{product.name}</div>
                        <div className="product-id">{product.id}</div>
                      </div>
                    </div>
                  </td>
                  <td>{product.category}</td>
                  <td>${parseFloat(product.price || '0').toFixed(2)}</td>
                  <td>
                    <div className="quantity-cell">
                      <input
                        type="number"
                        min="0"
                        value={quantity}
                        onChange={(e) => updateQuantity(product.id, e.target.value)}
                        className="quantity-input"
                      />
                      {quantity === 0 && <span className="stock-status out">❌</span>}
                      {quantity > 0 && quantity < 10 && <span className="stock-status low">⚠️</span>}
                      {quantity >= 10 && <span className="stock-status ok">✅</span>}
                    </div>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={product.popularityScore || 0}
                      onChange={(e) => updatePopularity(product.id, e.target.value)}
                      className="popularity-input"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredProducts.length === 0 && (
        <div className="no-results">
          {t('inventoryMgr.noProducts')}
        </div>
      )}

      <div className="inventory-actions">
        <button className="export-button">
          {t('inventoryMgr.exportCsv')}
        </button>
        <button 
          className={`save-button ${hasChanges ? 'has-changes' : ''}`}
          onClick={handleSaveChanges}
          disabled={!hasChanges || saving}
        >
          {saving 
            ? t('inventoryMgr.saving')
            : t('inventoryMgr.saveChanges')
          }
          {hasChanges && !saving && ' *'}
        </button>
      </div>
    </div>
  );
};

export default InventoryManager;
